import React,{useMemo,useState} from 'react';
import {useApp} from '../contexts/AppContext';
import {startOfMonth,endOfMonth,subMonths,format} from 'date-fns';
import * as Fi from 'react-icons/fi';
import {REPORT_TYPES,buildFinancialReports,downloadFinancialReport} from '../services/exportService.js';

const shell='rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_30px_rgba(3,45,96,0.035)]';
const button='inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold cursor-pointer transition-all hover:shadow-sm active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-60 disabled:cursor-wait';
const chartColours=['#60a5fa','#34cfa1','#fb7185','#a78bfa','#fbbf24','#38bdf8'];
const money=(value,currency='AUD')=>{
 try{return new Intl.NumberFormat('en-AU',{style:'currency',currency}).format(Number(value)||0)}
 catch{return 'AUD '+(Number(value)||0).toFixed(2)}
};
const percentage=n=>Number(n||0).toFixed(1)+'%';
const number=n=>Number(n||0).toLocaleString('en-AU');
const isPercent=s=>String(s).includes('%');
const isCount=s=>/entries|rules|months|people|categories|records|payments|count|io[u]?s/i.test(s);
const valueString=(v,key,currency)=>{
 if(v==null)return '–';
 if(typeof v!=='number')return String(v);
 if(isPercent(key))return percentage(v);
 if(isCount(key))return number(v);
 return money(v,currency);
};
const oneLine=(value,max=37)=>{
 const text=String(value??'');
 return text.length>max?text.slice(0,max-1)+'…':text;
};
function SummaryCard({title,value,sub,Icon,shade,colour}){
 return <article className={shell+' flex min-w-0 items-center gap-4 p-4 md:p-5'}>
  <div className={'grid h-12 w-12 shrink-0 place-items-center rounded-2xl '+shade}>
   <Icon size={23} className={colour}/>
  </div>
  <div className="min-w-0">
   <p className="text-sm font-medium text-slate-500">{title}</p>
   <p className="mt-1 text-[22px] font-extrabold leading-tight tracking-tight text-[#072d60] truncate" title={value}>{value}</p>
   <p className="mt-1 text-xs text-slate-500">{sub}</p>
  </div>
 </article>;
}
function PreviewChart({report,currency}){
 const entries=(report.chart||[]).slice(0,7);
 if(!entries.length)return <div className="flex min-h-52 items-center justify-center rounded-xl bg-slate-50 text-center text-sm text-slate-500">No chart data for this report yet.</div>;
 const max=Math.max(1,...entries.flatMap(r=>[Math.max(0,Number(r.primary)||0),Math.max(0,Number(r.secondary)||0)]));
 const dual=entries.some(r=>r.secondary!=null);
 const width=460,height=225,padX=38,chartHeight=134,base=174;
 const slot=(width-2*padX)/entries.length;
 return <div className="min-w-0">
  <div className="mb-3 flex flex-wrap items-center justify-end gap-4 text-[11px] text-slate-500">
   <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-400"/> {report.id==='budget'?'Budget':dual?(report.id==='cashflow'?'Cash received':'Income'):'Total'}</span>
   {dual&&<span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-400"/> {report.id==='budget'?'Actual':report.id==='cashflow'?'Cash paid':'Expenses'}</span>}
  </div>
  <svg viewBox={'0 0 '+width+' '+height} role="img" aria-label={report.name+' data preview'} className="w-full overflow-visible">
   {[0,0.5,1].map(p=>{
    const y=base-chartHeight*p;
    return <g key={p}><line x1={padX} y1={y} x2={width-12} y2={y} stroke="#e8eef7" strokeDasharray="4 4"/>
     <text x={padX-9} y={y+4} fill="#94a3b8" textAnchor="end" fontSize="10">{p===0?'0':p===1?Number(max).toLocaleString('en-AU',{notation:'compact',maximumFractionDigits:1}):''}</text></g>;
   })}
   {entries.map((r,i)=>{
    const x=padX+i*slot+slot/2,first=Math.max(0,Number(r.primary)||0)/max*chartHeight;
    const second=Math.max(0,Number(r.secondary)||0)/max*chartHeight;
    const bar=Math.min(23,slot/3);
    return <g key={i}>
     <rect x={x-(dual?bar+2:bar/2)} y={base-first} width={bar} height={first} fill="#60a5fa" rx="3"/>
     {dual&&<rect x={x+2} y={base-second} width={bar} height={second} fill="#34cfa1" rx="3"/>}
     <text x={x} y={base+17} fill="#64748b" fontSize="10" textAnchor="middle">{oneLine(r.name,10)}</text>
    </g>;
   })}
  </svg>
 </div>;
}
function DataPreview({report,currency}){
 const headers=report.columns.slice(0,5);
 return <div className="overflow-x-auto rounded-xl border border-slate-100 mt-2">
  <table className="w-full text-xs min-w-[380px]">
   <thead className="bg-[#f2f6fc] text-[#032d60]">
    <tr>{headers.map(h=><th key={h} className="text-left font-bold p-2.5 whitespace-nowrap">{h}</th>)}</tr>
   </thead>
   <tbody className="divide-y divide-slate-100">
    {report.rows.slice(0,5).map((r,i)=><tr key={i} className={i%2?'bg-slate-50/80':'bg-white'}>
     {r.slice(0,headers.length).map((cell,j)=><td key={j} className="px-2.5 py-2 text-slate-600 whitespace-nowrap" title={String(cell??'')}>{oneLine(valueString(cell,headers[j],currency),22)}</td>)}
    </tr>)}
    {!report.rows.length&&<tr><td colSpan={headers.length} className="py-8 text-center text-slate-400">No records in this period. Exports remain available with a clear empty-data message.</td></tr>}
   </tbody>
  </table>
 </div>;
}
export default function Reports(){
 const {state}=useApp();
 const {transactions=[],ious=[],categories=[],budgets=[],people=[],recurringRules=[],settings={},ui={}}=state;
 const [period,setPeriod]=useState('thismonth');
 const [customStart,setCustomStart]=useState('');
 const [customEnd,setCustomEnd]=useState('');
 const [account,setAccount]=useState('');
 const [basis,setBasis]=useState(ui.accountingMode||'accrual');
 const [selected,setSelected]=useState('budget');
 const [notice,setNotice]=useState(null);
 const [working,setWorking]=useState('');
 const asOf=useMemo(()=>new Date(),[]);
 const accounts=useMemo(()=>[...new Set(transactions.filter(t=>!t.isDeleted&&t.account).map(t=>String(t.account)))].sort(),[transactions]);
 const boundaries=useMemo(()=>{
  const first=new Date(asOf.getFullYear(),asOf.getMonth(),1);
  let start=first,end=endOfMonth(asOf);
  if(period==='last3months')start=startOfMonth(subMonths(asOf,2));
  if(period==='last6months')start=startOfMonth(subMonths(asOf,5));
  if(period==='last12months')start=startOfMonth(subMonths(asOf,11));
  if(period==='custom'&&customStart&&customEnd){
   const from=new Date(customStart+'T12:00:00');
   const to=new Date(customEnd+'T12:00:00');
   if(!Number.isNaN(from.getTime())&&!Number.isNaN(to.getTime())){start=from;end=to}
  }
  return {start,end};
 },[period,customStart,customEnd,asOf]);
 const periodValid=period!=='custom'||!!(customStart&&customEnd&&customStart<=customEnd);
 const model=useMemo(()=>periodValid?buildFinancialReports({
  transactions,ious,categories,budgets,people,recurringRules,settings,
  mode:basis,view:ui.currentView||'current',account,
  start:boundaries.start,end:boundaries.end,asOf
 }):null,[transactions,ious,categories,budgets,people,recurringRules,settings,basis,ui.currentView,account,boundaries.start,boundaries.end,asOf,periodValid]);
 const report=model?.reports.find(x=>x.id===selected)||model?.reports[0];
 const totals=model?.totals||{income:0,expenses:0,net:0,transactions:0};
 const bud=model?.reports.find(r=>r.id==='budget');
 const budgetLimit=bud?.kpis[0]?.[1]||0,budgetSpent=bud?.kpis[1]?.[1]||0;
 const exportNow=(id,type)=>{
  if(!model||working)return;
  setWorking(type+'-'+id);
  try{
   const filename=downloadFinancialReport(model,id,type);
   setNotice({kind:'success',message:'Your '+(id==='all'?'all-reports':model.reports.find(r=>r.id===id)?.name||id)+' '+type.toUpperCase()+' export has been downloaded: '+filename});
  }catch(error){
   console.error('Report export failed',error);
   setNotice({kind:'error',message:error.message||'Unable to generate the report. Your records have not been changed.'});
  }finally{setWorking('')}
 };
 const dateLabel=model?format(boundaries.start,'d MMM yyyy')+' – '+format(boundaries.end,'d MMM yyyy'):'Choose a valid date range';
 const chartTitle=report?.id==='budget'?'Budget vs Actual':report?.id==='spending'?'Spending by Category':report?.name||'Preview';
 return <div className="space-y-4 md:space-y-5 pb-12">
  <header className="flex flex-wrap items-start justify-between gap-3">
   <div>
    <h1 className="text-3xl font-extrabold tracking-tight text-slate-950">Reports</h1>
    <p className="mt-1 text-sm text-slate-500">Analyse, preview and export your financial data.</p>
   </div>
   <div className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800"><Fi.FiShield/> Frontend reports. Your Google Sheet is unchanged.</div>
  </header>
  <section aria-label="Report financial summary" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
   <SummaryCard title="Total Income" value={money(totals.income,settings.currency)} sub="Recognised in selected period" Icon={Fi.FiTrendingUp} shade="bg-emerald-50" colour="text-emerald-600"/>
   <SummaryCard title="Total Expenses" value={money(totals.expenses,settings.currency)} sub="Recognised in selected period" Icon={Fi.FiTrendingDown} shade="bg-rose-50" colour="text-rose-500"/>
   <SummaryCard title="Net Position" value={money(totals.net,settings.currency)} sub="Income minus expenses" Icon={Fi.FiActivity} shade="bg-blue-50" colour="text-blue-600"/>
   <SummaryCard title="Budget Utilisation" value={budgetLimit?percentage(budgetSpent/budgetLimit*100):'N/A'} sub={budgetLimit?money(budgetSpent,settings.currency)+' of '+money(budgetLimit,settings.currency):'No budgets for this period'} Icon={Fi.FiTarget} shade="bg-violet-50" colour="text-violet-600"/>
  </section>
  <section aria-label="Report filters and quick exports" className={shell+' p-4 flex flex-wrap items-end gap-3'}>
   <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 min-w-[145px]">
    Period
    <select value={period} onChange={e=>{setPeriod(e.target.value);setNotice(null)}} className="min-h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 cursor-pointer">
     <option value="thismonth">This Month</option><option value="last3months">Last 3 Months</option>
     <option value="last6months">Last 6 Months</option><option value="last12months">Last 12 Months</option>
     <option value="custom">Custom period</option>
    </select>
   </label>
   {period==='custom'&&<>
    <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">From
     <input aria-label="Report start date" type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} className="min-h-11 rounded-xl border border-slate-200 px-3 text-sm"/>
    </label>
    <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">To
     <input aria-label="Report end date" type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} className="min-h-11 rounded-xl border border-slate-200 px-3 text-sm"/>
    </label>
   </>}
   <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 min-w-[145px]">
    Account
    <select aria-label="Report account" value={account} onChange={e=>setAccount(e.target.value)} className="min-h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 cursor-pointer">
     <option value="">All accounts</option>{accounts.map(x=><option key={x} value={x}>{x}</option>)}
    </select>
   </label>
   <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 min-w-[125px]">
    Accounting
    <select aria-label="Report accounting basis" value={basis} onChange={e=>setBasis(e.target.value)} className="min-h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 cursor-pointer">
     <option value="accrual">Accrual</option><option value="cash">Cash</option>
    </select>
   </label>
   <div className="flex-1"/>
   <div className="flex flex-wrap items-center gap-2">
    <button type="button" disabled={!model||Boolean(working)} onClick={()=>exportNow('all','csv')} className={button+' border border-blue-200 bg-white text-[#0176D3] hover:bg-blue-50'} title="One ZIP file containing ten individual CSV reports"><Fi.FiDownload/> Export all CSV</button>
    <button type="button" disabled={!model||Boolean(working)} onClick={()=>exportNow('all','pdf')} className={button+' border border-blue-200 bg-white text-[#0176D3] hover:bg-blue-50'} title="One multipage PDF containing all reports"><Fi.FiFileText/> Export all PDF</button>
   </div>
  </section>
  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 px-1">
   <span><Fi.FiCalendar className="inline mr-1.5 text-blue-600"/>{dateLabel} · {basis==='cash'?'Posted cash':'Accrual'} · {(ui.currentView||'current')==='future'?'Future projections':'Actual/current'}</span>
   <span>Reports use data already loaded in Expense Manager</span>
  </div>
  {!periodValid&&<div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Select both custom dates and ensure the start date is not later than the end date.</div>}
  {notice&&<div role={notice.kind==='error'?'alert':'status'} aria-live="polite" className={'flex items-start gap-2 rounded-xl border p-3 text-sm '+(notice.kind==='error'?'border-rose-200 bg-rose-50 text-rose-900':'border-emerald-200 bg-emerald-50 text-emerald-900')}>
    {notice.kind==='error'?<Fi.FiAlertCircle className="shrink-0 mt-0.5"/>:<Fi.FiCheckCircle className="shrink-0 mt-0.5"/>}<span>{notice.message}</span>
   </div>}
  <div className="grid grid-cols-1 2xl:grid-cols-[1.5fr_1fr] gap-4">
   <section aria-label="Report library" className={shell+' min-w-0 p-4 md:p-5'}>
    <div className="flex items-center gap-3 mb-5">
     <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-600"><Fi.FiLayers size={20}/></div>
     <div><h2 className="text-lg font-bold text-slate-950">Report Library</h2><p className="text-xs text-slate-500">Choose a report to preview or export instantly.</p></div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
     {REPORT_TYPES.map(item=>{
       const Icon=Fi[item.icon]||Fi.FiFileText,active=selected===item.id;
       return <div key={item.id} className={'group min-w-0 rounded-xl border p-3 transition-all '+(active?'border-blue-500 bg-[#f2f7ff] shadow-[0_3px_16px_rgba(1,118,211,0.08)]':'border-slate-200 bg-white hover:border-blue-300')}>
        <button type="button" aria-pressed={active} onClick={()=>{setSelected(item.id);setNotice(null)}}
         className="flex w-full items-center gap-3 text-left cursor-pointer focus-visible:outline-2 focus-visible:outline-blue-600">
         <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-600"><Icon size={21}/></span>
         <span className="flex-1 min-w-0">
          <span className="block text-sm font-extrabold text-slate-900">{item.name}</span>
          <span className="block mt-1 text-[11px] leading-[1.4] text-slate-500">{item.description}</span>
         </span>
         <Fi.FiChevronRight className={'shrink-0 transition-transform '+(active?'text-blue-600':'text-slate-400 group-hover:translate-x-1')}/>
        </button>
        <div className="flex flex-wrap gap-2 mt-3 ml-14" aria-label={item.name+' export actions'}>
         <button type="button" disabled={!model||Boolean(working)} onClick={()=>exportNow(item.id,'csv')}
           aria-label={'Export '+item.name+' CSV'} className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-100 focus-visible:outline-2 focus-visible:outline-emerald-600 disabled:opacity-50">
           <Fi.FiDownload size={12}/> CSV
         </button>
         <button type="button" disabled={!model||Boolean(working)} onClick={()=>exportNow(item.id,'pdf')}
           aria-label={'Export '+item.name+' PDF'} className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] font-bold text-rose-700 hover:bg-rose-100 focus-visible:outline-2 focus-visible:outline-rose-600 disabled:opacity-50">
           <Fi.FiFileText size={12}/> PDF
         </button>
        </div>
       </div>;
     })}
    </div>
   </section>
   <section aria-label="Selected report preview" className={shell+' min-w-0 p-4 md:p-5'}>
    <div className="flex gap-3 items-start justify-between mb-4">
     <div className="flex items-center gap-3 min-w-0">
      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-600 shrink-0"><Fi.FiEye size={19}/></div>
      <div className="min-w-0"><h2 className="font-bold text-slate-950 text-base">Report Preview</h2><p className="text-xs text-slate-500 truncate">{report?.name||'Choose a report'}</p></div>
     </div>
     <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">CSV + PDF</span>
    </div>
    {report&&model&&<>
     <div className="rounded-xl border border-slate-200 p-3 md:p-4">
      <h3 className="font-bold text-slate-800 text-sm">{chartTitle}</h3>
      <p className="text-xs text-slate-500 mb-3">{report.rows.length} data rows in this report</p>
      <PreviewChart report={report} currency={model.currency}/>
     </div>
     <div className="mt-4 rounded-xl border border-slate-200 bg-[#f7faff] p-3">
      <h3 className="font-bold text-slate-800 text-sm mb-3">Key figures for selected period</h3>
      <div className={'grid gap-2 '+(report.kpis.length>=3?'grid-cols-3':'grid-cols-2')}>
       {(report.kpis.length?report.kpis:[['Records',report.rows.length]]).slice(0,3).map(([label,val])=>
        <div className="min-w-0" key={label}><p className="text-[11px] text-slate-500">{label}</p>
         <p title={valueString(val,label,model.currency)} className="mt-1 text-sm font-extrabold text-[#032d60] truncate">{valueString(val,label,model.currency)}</p></div>)}
      </div>
     </div>
     <div className="mt-4">
      <h3 className="font-bold text-slate-800 text-sm mb-2">Data preview</h3>
      <DataPreview report={report} currency={model.currency}/>
     </div>
     <div className="mt-4 flex gap-2 items-start text-xs text-slate-600">
      <Fi.FiInfo className="text-blue-600 shrink-0 mt-0.5"/>
      <span>{report.notes?.[0]||report.description} CSV includes the complete report rows; PDF includes a branded summary, chart and paginated table.</span>
     </div>
     <div className="mt-5 grid grid-cols-2 gap-3">
      <button type="button" disabled={Boolean(working)} onClick={()=>exportNow(selected,'csv')} className={button+' bg-[#0176D3] text-white hover:bg-[#0066b8]'}><Fi.FiDownload/> Export CSV</button>
      <button type="button" disabled={Boolean(working)} onClick={()=>exportNow(selected,'pdf')} className={button+' border border-blue-200 bg-white text-[#0176D3] hover:bg-blue-50'}><Fi.FiFileText/> Export PDF</button>
     </div>
    </>}
   </section>
  </div>
  <div className="flex gap-2 items-center rounded-xl bg-[#eaf2ff] px-4 py-3 text-xs font-semibold text-blue-800"><Fi.FiShield className="shrink-0" size={18}/> No backend or Google Sheet changes: every report is calculated from your existing frontend data and exported directly to your device.</div>
 </div>;
}